import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  Typography,
  Grid,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const DelayOrderModal = ({ open, onClose, onSubmit, t, isRTL }) => {
  const [selectedDate, setSelectedDate] = useState(null); // 'today' or 'tomorrow'
  const [selectedTime, setSelectedTime] = useState(null); // e.g., '8:00 - 12:00'

  const timeSlots = [
    "8:00 - 12:00",
    "12:00 - 16:00",
    "16:00 - 20:00",
    "20:00 - 24:00",
  ];

  useEffect(() => {
    if (selectedDate === "today" && selectedTime) {
      const GRACE_PERIOD_HOURS = 2;
      const currentHour = new Date().getHours();
      const slotStartHour = parseInt(selectedTime.split(" - ")[0], 10);
      const cutoffHour = slotStartHour + GRACE_PERIOD_HOURS;

      if (currentHour >= cutoffHour) {
        setSelectedTime(null);
      }
    }
  }, [selectedDate, selectedTime, open]);

  useEffect(() => {
    if (!open) {
      setSelectedDate(null);
      setSelectedTime(null);
    }
  }, [open]);

  const handleSave = () => {
    const deliveryDay = new Date();
    deliveryDay.setHours(0, 0, 0, 0);

    if (selectedDate === "tomorrow") {
      deliveryDay.setDate(deliveryDay.getDate() + 1);
    }

    const [startStr, endStr] = selectedTime.split(" - ");
    const startHour = parseInt(startStr, 10);
    const endHour = parseInt(endStr, 10);

    const startDate = new Date(deliveryDay);
    startDate.setHours(startHour);

    const endDate = new Date(deliveryDay);
    if (endHour === 24) {
      endDate.setDate(endDate.getDate() + 1);
      endDate.setHours(0);
    } else {
      endDate.setHours(endHour);
    }

    onSubmit({
      start: startDate.toISOString(),
      end: endDate.toISOString(),
    });
    onClose();
  };

  const isSaveDisabled = !selectedDate || !selectedTime;

  const renderButton = (
    label,
    value,
    isSelected,
    onClick,
    disabled = false
  ) => (
    <Button
      fullWidth
      variant={isSelected ? "contained" : "outlined"}
      onClick={onClick}
      disabled={disabled}
      sx={{
        borderColor: "#2A3467",
        color: isSelected ? "#fff" : "#2A3467",
        backgroundColor: isSelected ? "#2A3467" : "#fff",
        "&:hover": {
          backgroundColor: isSelected ? "#202850" : "rgba(42, 52, 103, 0.04)",
        },
        "&:disabled": {
          borderColor: "rgba(0, 0, 0, 0.26)",
          color: "rgba(0, 0, 0, 0.26)",
        },
      }}
    >
      {label}
    </Button>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          padding: "24px",
        },
      }}
    >
      <DialogTitle>
        {t("rescheduleOrder")}
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 8,
            right: isRTL ? "auto" : 8,
            left: isRTL ? 8 : "auto",
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography sx={{ mb: 2 }}>{t("chooseDateTimeSlot")}</Typography>
        <Box>
          <Typography variant="h6" gutterBottom>
            {t("date")}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              {renderButton(t("today"), "today", selectedDate === "today", () =>
                setSelectedDate("today")
              )}
            </Grid>
            <Grid item xs={6}>
              {renderButton(
                t("tomorrow"),
                "tomorrow",
                selectedDate === "tomorrow",
                () => setSelectedDate("tomorrow")
              )}
            </Grid>
          </Grid>
        </Box>
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            {t("time")}
          </Typography>
          <Grid container spacing={1}>
            {timeSlots.map((slot) => {
              let isSlotDisabled = false;
              if (selectedDate === "today") {
                const GRACE_PERIOD_HOURS = 2;
                const currentHour = new Date().getHours();
                const slotStartHour = parseInt(slot.split(" - ")[0], 10);
                const cutoffHour = slotStartHour + GRACE_PERIOD_HOURS;
                isSlotDisabled = currentHour >= cutoffHour;
              }

              return (
                <Grid item xs={3} key={slot}>
                  {renderButton(
                    slot,
                    slot,
                    selectedTime === slot,
                    () => setSelectedTime(slot),
                    isSlotDisabled
                  )}
                </Grid>
              );
            })}
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: "12px 0" }}>
        <Button
          onClick={handleSave}
          variant="outlined"
          disabled={isSaveDisabled}
          sx={{
            color: "#fff",
            borderColor: "#2A3467",
            padding: "8px 35px",
            backgroundColor: "#2A3467",
            "&:hover": {
              backgroundColor: "#fff",
              color: "#2A3467",
              borderColor: "#2A3467",
            },

            "&:disabled": {
              backgroundColor: "#cccccc",
              color: "#888888",
              borderColor: "#cccccc",
            },
          }}
        >
          {t("save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DelayOrderModal;
